import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAllPages } from '../../lib/api';
import { leadTimesAPI, type SupplierLeadTime } from '../../lib/api/leadTimes';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useView } from '../../contexts/ViewContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ListPager, ListSearch, useListControls } from '../../components/common/listControls';
import { SortableTh } from '../../components/common/SortableTh';
import { UploadTableEmpty, UploadTableSkeleton } from '../barratt/upload-history/UploadTableStates';
import { formatDay } from '../barratt/uploadCycle';
import { AlertCircle, Download, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import LeadTimeDialog from './LeadTimeDialog';

type Filter = 'current' | 'all';

/**
 * The SLA lead-time register. Customer users maintain it; a supplier sees the
 * rows agreed with it, read-only. Rows in force today are the default view.
 */
export default function LeadTimesPage() {
  const { portalMode, getViewQueryParams, viewKey } = useView();
  const readOnly = portalMode === 'supplier';
  const [rows, setRows] = useState<SupplierLeadTime[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('current');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SupplierLeadTime | null>(null);
  const [deleting, setDeleting] = useState<SupplierLeadTime | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    const scope: Record<string, string | number | boolean> = {};
    Object.entries(getViewQueryParams()).forEach(([k, v]) => { if (v !== undefined) scope[k] = v; });
    setLoading(true);
    setError(null);
    fetchAllPages<SupplierLeadTime>((page) => leadTimesAPI.list({ ...scope, page, page_size: 500 }))
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Lead times load failed:', err);
        setError(getErrorMessage(err, 'Could not load lead times'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [getViewQueryParams, viewKey, tick]);

  const filtered = useMemo(() => filter === 'current' ? rows.filter((r) => r.is_current) : rows, [rows, filter]);
  const list = useListControls(filtered, {
    searchText: (r) => [r.supplier_name, r.commodity_code, r.commodity_name, r.notes].join(' '),
    sorters: {
      supplier: (r) => r.supplier_name.toLowerCase(),
      commodity: (r) => r.commodity_code || '',
      days: (r) => r.lead_time_days,
      from: (r) => r.effective_from,
      to: (r) => r.effective_to ?? '9999-12-31',
    },
    defaultSort: { key: 'supplier', dir: 'asc' },
    tieBreak: (r) => `${r.commodity_code}|${r.effective_from}`,
    resetKeys: [filter],
  });
  const currentCount = useMemo(() => rows.filter((r) => r.is_current).length, [rows]);

  const remove = async () => {
    if (!deleting) return;
    try {
      await leadTimesAPI.remove(deleting.id);
      toast.success('Lead time removed');
      reload();
    } catch (err: unknown) {
      console.error('Lead time delete failed:', err);
      toast.error(getErrorMessage(err, 'Could not remove the lead time'));
    } finally {
      setDeleting(null);
    }
  };

  const exportExcel = () => {
    const sheet = XLSX.utils.aoa_to_sheet([
      ['Supplier', 'Commodity code', 'Commodity', 'Lead time (days)', 'From', 'Until', 'Notes'],
      ...list.orderedRows.map((r) => [r.supplier_name, r.commodity_code, r.commodity_name, r.lead_time_days, r.effective_from, r.effective_to ?? '', r.notes]),
    ]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Lead times');
    XLSX.writeFile(book, `lead-times-${filter}.xlsx`);
  };

  const columns = 6 + (readOnly ? 0 : 1);
  const emptyMessage = rows.length === 0
    ? (readOnly ? 'No lead times have been agreed with your company yet.' : 'No lead times yet — add the first supplier\'s agreed days.')
    : list.search ? `No lead times match "${list.search}".` : 'No lead times in force today. Switch to All to see past and future rows.';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{readOnly ? 'My Lead Times' : 'Lead Times'}</h1>
        <p className="mt-1 max-w-3xl text-muted-foreground">
          {readOnly
            ? 'The agreed days from call-off to delivery your customer measures you against. Your monthly file\'s SLA date is calculated from these.'
            : 'The SLA register: agreed days from call-off to delivery per supplier, with commodity-specific exceptions. The monthly Supplier Performance file is scored against these instead of the SLA date the supplier declares.'}
        </p>
      </div>

      {error ? (
        <Card><CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          <AlertCircle className="h-10 w-10 text-red-500" />
          <p className="text-sm font-medium">Could not load lead times</p>
          <p className="max-w-md text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" onClick={reload}><RefreshCw className="mr-2 h-4 w-4" />Retry</Button>
        </CardContent></Card>
      ) : (
        <>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
              <TabsList data-testid="lead-times-filter">
                <TabsTrigger value="current">In force today ({currentCount})</TabsTrigger>
                <TabsTrigger value="all">All ({rows.length})</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <div className="md:w-72">
                <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search supplier, commodity, notes…" testId="lead-times-search" />
              </div>
              <Button variant="outline" size="sm" onClick={exportExcel} disabled={list.orderedRows.length === 0} data-testid="lead-times-export">
                <Download className="mr-2 h-4 w-4" />Export
              </Button>
              {!readOnly && (
                <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="lead-times-add">Add lead time</Button>
              )}
            </div>
          </div>

          <Card><CardContent className="p-0">
            <ListPager list={list} position="top" noun="lead times" testIdPrefix="lead-times" />
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <SortableTh field="supplier" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Supplier</SortableTh>
                  <SortableTh field="commodity" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Commodity</SortableTh>
                  <SortableTh field="days" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">Lead time</SortableTh>
                  <SortableTh field="from" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>From</SortableTh>
                  <SortableTh field="to" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Until</SortableTh>
                  <TableHead>Notes</TableHead>
                  {!readOnly && <TableHead className="text-right">Actions</TableHead>}
                </TableRow></TableHeader>
                {loading ? <UploadTableSkeleton columns={columns} />
                  : list.pageRows.length === 0 ? <UploadTableEmpty columns={columns} message={emptyMessage} />
                  : (
                    <TableBody>
                      {list.pageRows.map((r) => (
                        <TableRow key={r.id} data-testid={`lead-time-${r.id}`}>
                          <TableCell className="font-medium">{r.supplier_name}</TableCell>
                          <TableCell>
                            {r.commodity_code ? (
                              <><span className="font-mono text-xs">{r.commodity_code}</span>
                                {r.commodity_name && <span className="text-muted-foreground"> · {r.commodity_name}</span>}</>
                            ) : <Badge variant="outline" className="bg-muted text-muted-foreground">All commodities</Badge>}
                          </TableCell>
                          <TableCell className="text-right"><span className="font-semibold">{r.lead_time_days}</span> <span className="text-muted-foreground">days</span></TableCell>
                          <TableCell>{formatDay(r.effective_from)}</TableCell>
                          <TableCell>{r.effective_to ? formatDay(r.effective_to) : <span className="text-muted-foreground">Open-ended</span>}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.notes || '—'}</TableCell>
                          {!readOnly && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" aria-label="Edit" onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`lead-time-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="sm" aria-label="Remove" onClick={() => setDeleting(r)} data-testid={`lead-time-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  )}
              </Table>
            </div>
            <ListPager list={list} position="bottom" noun="lead times" testIdPrefix="lead-times" />
          </CardContent></Card>
        </>
      )}

      {!readOnly && (
        <>
          <LeadTimeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} row={editing} onSaved={reload} />
          <AlertDialog open={deleting !== null} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove this lead time?</AlertDialogTitle>
                <AlertDialogDescription>
                  {deleting ? `${deleting.supplier_name} · ${deleting.commodity_code || 'all commodities'} · ${deleting.lead_time_days} days. ` : ''}
                  Future uploads fall back to the SLA date in the supplier's file. Figures already computed are kept.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction onClick={remove} data-testid="lead-time-delete-confirm">Remove</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
